import { capitalize, get, snakeCase, trimStart, uniq } from "lodash";
import moment from "moment";
import React from "react";
import isFQDN from "validator/es/lib/isFQDN";
import isIP from "validator/es/lib/isIP";
import isURL from "validator/es/lib/isURL";
import { NotificationsLevel, TimeEnum } from "./lib/enums";
import { LOCATION } from "./modules/search/consts";
import { show as showNotification } from "./notifications/notifications";
import { enums, route } from "./shim/jsbridge";

const ISO_NO_MILLIS_FORMAT_UTC = "YYYY-MM-DD[T]HH:mm:ss[Z]";

// file type and file subtype delimiter contains trailing and leading spaces
// MachO file type also contain space in file type name (e.g. MachO64 Little / Exe)
export const stripFileType = (fileType = "") => fileType.replaceAll(" / ", "/");

export const pluralize = (value, size) => {
  if (!value) return value;
  if (size === 1) {
    return value;
  }
  return `${value}s`;
};

export const splitList = (array, index) => {
  const [first, second] = [array.slice(0, index), array.slice(index)];
  return [first, second];
};

export const formatDate = (momentDate, format = ISO_NO_MILLIS_FORMAT_UTC) =>
  momentDate.format(format);

export const utcTimestampFromString = value => {
  let momentDate;

  switch (value) {
    case TimeEnum.ALL:
      return null;
    case TimeEnum.NOW:
      momentDate = moment();
      break;
    case TimeEnum.TODAY:
      momentDate = moment().subtract(1, "day");
      break;
    case TimeEnum.LAST_WEEK:
      momentDate = moment().subtract(1, "week");
      break;
    case TimeEnum.LAST_MONTH:
      momentDate = moment().subtract(1, "month");
      break;
    case TimeEnum.LAST_QUARTER:
      momentDate = moment().subtract(3, "months");
      break;
    case TimeEnum.LAST_YEAR:
      momentDate = moment().subtract(1, "year");
      break;
    default:
      momentDate = moment(value);
  }

  return formatDate(momentDate.utc());
};

/**
 * Checks if hash is valid SHA1 hash. Used for enabling subscriptions only for SHA1 hashes.
 * @param hash
 * @returns true if hash is valid SHA1, otherwise false
 */
export const isValidSha1Hash = hash => {
  if (!hash) return false;
  return /^[a-fA-F0-9]{40}$/.test(hash);
};

/**
 * Takes an input string and splits it into an array based on comma, blank space or newline
 * @param {string} inputValue
 * @returns {array} an array of strings
 */
export const splitSelectInput = inputValue =>
  uniq(inputValue.split(/[,\s\n]+/).filter(item => item.length > 0));

/**
 * Takes an input string and filters out anything that is not a valid SHA1 hash
 * @param {string} inputValue
 * @returns {array} an array of valid SHA1 hashes
 */
export const validateHashInput = inputValue => {
  const inputArray = splitSelectInput(inputValue);
  const initialValue = {
    validHashes: [],
    invalidHashes: []
  };
  return inputArray.reduce((acc, inputItem) => {
    if (isValidSha1Hash(inputItem)) {
      acc.validHashes.push(inputItem);
    } else {
      acc.invalidHashes.push(inputItem);
    }
    return acc;
  }, initialValue);
};

/**
 * Highlights text in a textarea
 * Adapted from http://lostsource.com/2012/11/30/selecting-textarea-line.html
 * @param {object} textArea the textArea object to target
 * @param {number} startPos the highlight start position
 * @param {number} endPos the highlight end position
 * @returns {boolean} whether text was highlighted or not
 */
export const highlightTextArea = (textArea, startPos, endPos) => {
  // Chrome / Firefox
  if (typeof textArea.selectionStart !== "undefined") {
    textArea.focus();
    textArea.selectionStart = startPos;
    textArea.selectionEnd = endPos;
    return true;
  }

  // IE
  if (document.selection?.createRange) {
    textArea.focus();
    textArea.select();
    const range = document.selection.createRange();
    range.collapse(true);
    range.moveEnd("character", endPos);
    range.moveStart("character", startPos);
    range.select();
    return true;
  }
  return false;
};

/**
 * Stringifies nested objects in url params object so that it can be used with querystring.stringify()
 * (see https://github.com/nodejs/node-v0.x-archive/issues/1665)
 *
 * @param params (object) a params object to flatten
 */
export const flattenUrlParams = params =>
  Object.keys(params).reduce((acc, k) => {
    if (typeof params[k] !== "object") {
      return Object.assign(acc, { [k]: params[k] });
    }
    return Object.assign(acc, { [k]: JSON.stringify(params[k]) });
  }, {});

/**
 * Finds intersection of 2 javascript arrays
 */
export const arrayIntersection = (arr1, arr2) =>
  arr1.filter(v => arr2.indexOf(v) !== -1);

export const defaultSort = Object.freeze({
  field: "submission_time",
  direction: "desc"
});

export const defaultCloudSort = Object.freeze({
  field: "first_seen",
  direction: "desc"
});

export const parseSortString = (sortString, location) => {
  if (!sortString) {
    return location === LOCATION.LOCAL ? defaultSort : defaultCloudSort;
  }
  const split = sortString.split(" ");
  return {
    field: split[0],
    direction: split[1]
  };
};

export const getURLGridQueryParams = qs => {
  const parsedQS = parseURLParams(trimStart(qs, "?"));
  const parsedSelectionsQS = parseURLParams(parsedQS.selections);
  const parsedLocation = parsedQS.location || "local";
  return {
    page: parseInt(parsedQS.page || 1, 10),
    pageSize: parseInt(parsedQS.pagesize || 100, 10),
    sort: parseSortString(parsedQS.sort, parsedLocation),
    searchQuery: parsedQS.q || "",
    filters: {
      location: parsedLocation
    },
    selections: parsedSelectionsQS || {}
  };
};

export const cookieName = "cookieConsent";

export const transformPlatformSelectionValues = (value = "") => {
  if (!value) {
    return undefined;
  }
  if (value === "MACOS_11") {
    return "macOS Big Sur";
  } else if (value === "UBUNTU_20") {
    return "Ubuntu 20 (Linux)";
  } else if (value === "ANDROID_12") {
    return "Android 12";
  } else {
    return capitalize(value.replace("_", " "));
  }
};

export const getMenuItemForPlatform = (platformKey, platformValue) => ({
  value: platformValue,
  label: transformPlatformSelectionValues(platformKey)
});

export const isLocaleAvailableForPlatform = platform =>
  [
    enums.platforms.WINDOWS_7,
    enums.platforms.WINDOWS_10,
    enums.platforms.WINDOWS_11
  ].includes(platform);

/**
 * Add polling behaviour to function returning promise, execute it max n times.
 * Function is called when previous iteration has finished with delay.
 * @param pollFn function used for polling, should return promise
 * @param stopConditionFn function used to evaluate if promise result satisfies defined condition, if yes resolve promise
 * else continue to poll
 * @param interval interval to use when polling
 * @param maxTries maximum time to try pool (-1 infinite)
 * @returns {Promise<unknown>}
 */
export const poll = (
  pollFn,
  stopConditionFn,
  interval = 5000,
  maxTries = 5
) => {
  let counter = maxTries;

  const expression = (resolve, reject) => {
    pollFn().then(
      response => {
        counter -= 1;
        if (stopConditionFn(response)) {
          resolve(response);
        } else if (counter === 0) {
          reject(`Max tries polling reached ${maxTries}`);
        } else {
          setTimeout(expression, interval, resolve, reject);
        }
      },
      rejectResponse => {
        reject(rejectResponse);
      }
    );
  };
  return new Promise(expression);
};

export const titleCase = string => string.split(" ").map(capitalize).join(" ");

export const underscoreToTitleCase = string =>
  string.split("_").map(capitalize).join(" ");

export const sortGridByColumn = (data, params) => {
  const { field, direction } = params;
  const sortOrder = direction === "asc" ? 1 : -1;
  return data.sort((a, b) => {
    const A = a[field] === null || a[field] === undefined ? "" : a[field];
    const B = b[field] === null || b[field] === undefined ? "" : b[field];

    const AValue = typeof A === "string" ? A.toUpperCase() : A;
    const BValue = typeof B === "string" ? B.toUpperCase() : B;

    if (AValue < BValue) {
      return sortOrder * -1;
    } else if (AValue > BValue) {
      return sortOrder * 1;
    } else {
      return 0;
    }
  });
};

export const toClassificationDisplayText = (inputString = "") =>
  toClassificationDisplayText.mapping[inputString.toLowerCase()] ?? "-";

toClassificationDisplayText.mapping = Object.freeze({
  known: "Goodware",
  goodware: "Goodware",
  unknown: "Unknown",
  malicious: "Malicious",
  malware: "Malicious",
  suspicious: "Suspicious"
});

export const calculatePaginationRange = (activePage, numPages, maxButtons) => {
  const pageRange = [];
  let currentPage = activePage;

  if (activePage < 1) {
    currentPage = 1;
  } else if (activePage > numPages) {
    currentPage = numPages;
  }

  let startPage = 1;
  let endPage = numPages;

  if (numPages > maxButtons) {
    const maxPagesBeforeCurrentPage = Math.floor(maxButtons / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxButtons / 2) - 1;
    if (currentPage <= maxPagesBeforeCurrentPage) {
      startPage = 1;
      endPage = maxButtons;
    } else if (currentPage + maxPagesAfterCurrentPage >= numPages) {
      startPage = numPages - maxButtons + 1;
      endPage = numPages;
    } else {
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  for (let i = startPage; i <= endPage; i += 1) {
    pageRange.push(i);
  }

  return pageRange;
};

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const isNotAllowedNginx = ({ headers: { server }, status }) =>
  server === "nginx" && status === 405;

export default {
  sleep,
  flattenUrlParams,
  highlightTextArea,
  utcTimestampFromString,
  arrayIntersection,
  titleCase,
  sortGridByColumn,
  transformPlatformSelectionValues
};

export const remapedUiClassificationLabel = classification =>
  classification?.toLowerCase() === "unknown"
    ? "Unknown (No Threats Found)"
    : titleCase(classification);

export const legacyNavigateTo = (location, useRoute = true) => {
  const redirectRoute = useRoute ? route(location) : location;
  window.location.href = redirectRoute;
};

export const isFilterActive = (data, filterLabel, filterItem) =>
  get(data, filterLabel, "") === filterItem;

export const isBlankOrHangulFiller = value => /^\s*$|\u3164/.test(value);

export const getSizeInBytes = value => new TextEncoder().encode(value).length;

export const convertBytesToMB = value => (value / Math.pow(1024, 2)).toFixed(2);

export const isAdmin = () => {
  const { _rlapp_user: { permissions } = {} } = window || {};
  return !!permissions?.find(item => item === "authentication.admin");
};

export const replaceAll = (value, replace, replaceWith) => {
  const regex = new RegExp(replace, "g");
  return value?.replace(regex, replaceWith);
};

export const stripSpaces = string => {
  if (!string) return null;
  return string.toString().replaceAll(" ", "");
};

export const isValidHttpUrl = value => {
  if (!value) return false;
  return isURL(value, {
    require_protocol: true,
    require_valid_protocol: true,
    protocols: ["http", "https"],
    allow_fragments: true,
    allow_query_components: true
  });
};

export const isNtiValue = value => {
  if (!value) return false;
  return isValidHttpUrl(value) || isFQDN(value) || isIP(value);
};

export const copyToClipboard = (value, subjectText = "Text") => {
  navigator.clipboard.writeText(value).then(
    () => {
      showNotification({
        level: NotificationsLevel.SUCCESS,
        message: `${subjectText} copied to clipboard!`
      });
    },
    () => {
      showNotification({
        level: NotificationsLevel.ERROR,
        message: `Oops, could not copy ${subjectText} to clipboard!`
      });
    }
  );
};

export const transformBrandNames = value => {
  if (!value) return undefined;

  const replaceNames = oldNameString =>
    oldNameString.replace(
      /TiCore|TitaniumCore|TiCloud|TitaniumCloud|Antivirus \(based on the RCA Classify\)|sample override/g,
      match => {
        switch (match) {
          case "TiCore":
          case "TitaniumCore":
            return "Spectra Core";
          case "TiCloud":
          case "TitaniumCloud":
            return "Spectra Intelligence";
          case "Antivirus (based on the RCA Classify)":
            return "Threat Reputation";
          case "sample override":
            return "Sample Override";
          default:
            return match;
        }
      }
    );

  if (typeof value === "string") return replaceNames(value);

  if (Array.isArray(value)) return value.map(item => transformBrandNames(item));

  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value);
    const newObj = entries.reduce((acc, [key, objValue]) => {
      if (typeof objValue === "string") {
        acc[key] = transformBrandNames(objValue);
      } else if (Array.isArray(objValue)) {
        acc[key] = objValue.map(item => transformBrandNames(item));
      } else if (typeof objValue === "object" && objValue !== null) {
        acc[key] = transformBrandNames(objValue);
      } else {
        acc[key] = objValue;
      }
      return acc;
    }, {});
    return newObj;
  }

  return value;
};

export const transformClassificationTypes = value => {
  if (!value) return undefined;

  const replaceNames = oldNameString =>
    oldNameString.replace(/AntiVirus/g, match => {
      switch (match) {
        case "AntiVirus":
          return "ReversingLabs RCA Algorithm";
        default:
          return match;
      }
    });

  if (typeof value === "string") return replaceNames(value);

  return value;
};

export const preventOnBeforeUnload = () => (window.onbeforeunload = null);

export const sanitizeTextForTestId = node => {
  const testIdString = React.isValidElement(node)
    ? React.Children.toArray(node.props.children).join("")
    : String(node);

  // Find the index of the first occurrence of '(' or ':'
  const cutoffIndex = testIdString.search(/[:(]/);

  const sanitizedString =
    cutoffIndex !== -1
      ? testIdString.slice(0, cutoffIndex).trim()
      : testIdString.trim();

  return snakeCase(sanitizedString);
};

export const generateRandomString = () =>
  Math.random().toString(30).substring(2);

/**
 *
 * @param {string} params
 * @returns {object}
 */
export const parseURLParams = params => {
  if (!params) return {};

  const parsedParams = new URLSearchParams(params);
  return Object.fromEntries(parsedParams);
};

/**
 *
 * @param {object} params
 * @returns {string}
 */
export const stringifyURLParams = params => {
  if (!params) return "";

  const parsedParams = new URLSearchParams(params);
  return parsedParams.toString();
};

export const getSampleDisplayNameFromSchemaData = data =>
  data.file_name || data.sha1;
